<template>
  <div class="front-layout">
    <header class="front-header" v-if="isLoggedIn">
      <div class="header-content">
        <AppLogo to="/" size="md" class="header-logo" />

        <div class="search-container">
          <el-input
            :placeholder="$t('frontLayout.searchPlaceholder')"
            prefix-icon="el-icon-search"
            v-model="searchQuery"
            @keyup.enter.native="handleSearch"
            class="search-input"
          />
        </div>

        <nav class="header-nav">
          <el-button
            type="primary"
            @click="$router.push('/publish')"
            class="publish-btn"
            icon="el-icon-plus"
          >
            {{ $t('frontLayout.publish') }}
          </el-button>

          <LanguageSwitcher />

          <el-dropdown @command="handleUserCommand" v-if="isLoggedIn" trigger="click">
            <div class="user-trigger">
              <el-avatar :size="34" class="user-avatar">
                {{ user.nickname?.charAt(0) || 'U' }}
              </el-avatar>
              <span class="user-name">{{ user.nickname || $t('frontLayout.user') }}</span>
              <i class="el-icon-arrow-down trigger-arrow"></i>
            </div>
            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item command="profile">
                <i class="el-icon-user"></i>{{ $t('frontLayout.profile') }}
              </el-dropdown-item>
              <el-dropdown-item command="wallet">
                <i class="el-icon-coin"></i>{{ $t('frontLayout.wallet') }}
              </el-dropdown-item>
              <el-dropdown-item command="words">
                <i class="el-icon-notebook-2"></i>{{ $t('frontLayout.words') }}
              </el-dropdown-item>
              <el-dropdown-item command="topics">
                <i class="el-icon-discover"></i>{{ $t('frontLayout.topics') }}
              </el-dropdown-item>
              <el-dropdown-item divided command="logout">
                <i class="el-icon-switch-button"></i>{{ $t('frontLayout.logout') }}
              </el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>

          <el-button
            v-if="!isLoggedIn"
            type="primary"
            @click="$router.push('/login')"
            class="login-btn"
          >
            {{ $t('frontLayout.login') }}
          </el-button>
        </nav>
      </div>
    </header>

    <main class="main-content" :class="{ 'no-header': !isLoggedIn }">
      <transition name="page" mode="out-in">
        <router-view :key="$route.fullPath" />
      </transition>
    </main>

    <footer class="mobile-nav" v-if="isMobile && isLoggedIn">
      <div class="mobile-nav-inner">
        <div class="nav-item" @click="$router.push('/')" :class="{ active: $route.name === 'home' }">
          <i class="el-icon-house"></i>
          <span>{{ $t('frontLayout.home') }}</span>
        </div>
        <div class="nav-item" @click="$router.push('/topics')" :class="{ active: $route.name === 'topics' }">
          <i class="el-icon-discover"></i>
          <span>{{ $t('frontLayout.topic') }}</span>
        </div>
        <div class="nav-item publish" @click="$router.push('/publish')">
          <i class="el-icon-plus"></i>
        </div>
        <div class="nav-item" @click="$router.push('/search')" :class="{ active: $route.name === 'search' }">
          <i class="el-icon-search"></i>
          <span>{{ $t('frontLayout.searchNav') }}</span>
        </div>
        <div class="nav-item" @click="$router.push('/profile')" :class="{ active: $route.name === 'profile' }">
          <i class="el-icon-user"></i>
          <span>{{ $t('frontLayout.mine') }}</span>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
export default {
  name: 'FrontLayout',
  components: {
    AppLogo: () => import('@/components/AppLogo.vue'),
    LanguageSwitcher: () => import('@/components/LanguageSwitcher.vue')
  },
  data() {
    return {
      searchQuery: '',
      isMobile: false,
      isLoggedIn: false,
      user: {
        nickname: '',
        avatar: ''
      }
    }
  },
  mounted() {
    this.checkMobile()
    this.checkLoginStatus()
    window.addEventListener('resize', this.checkMobile)
    this._afterEachUnsub = this.$router.afterEach(() => {
      this.checkLoginStatus()
    })
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkMobile)
    if (this._afterEachUnsub) {
      this._afterEachUnsub()
    }
  },
  methods: {
    checkMobile() {
      this.isMobile = window.innerWidth <= 768
    },
    checkLoginStatus() {
      const token = localStorage.getItem('token')
      this.isLoggedIn = !!token
      if (this.isLoggedIn) {
        try {
          const userData = JSON.parse(localStorage.getItem('user') || '{}')
          this.user = {
            nickname: userData.nickname || userData.name || userData.username || this.$t('frontLayout.user'),
            avatar: userData.avatar || ''
          }
        } catch {
          this.user = { nickname: this.$t('frontLayout.user'), avatar: '' }
        }
      }
    },
    handleSearch() {
      if (this.searchQuery.trim()) {
        this.$router.push(`/search?q=${encodeURIComponent(this.searchQuery)}`)
      }
    },
    handleUserCommand(command) {
      switch (command) {
        case 'profile':
          this.$router.push('/profile')
          break
        case 'wallet':
          this.$router.push('/wallet')
          break
        case 'words':
          this.$router.push('/words')
          break
        case 'topics':
          this.$router.push('/topics')
          break
        case 'logout':
          this.handleLogout()
          break
      }
    },
    async handleLogout() {
      try {
        await this.$confirm(this.$t('frontLayout.logoutConfirm'), this.$t('frontLayout.logoutConfirmTitle'), {
          confirmButtonText: this.$t('common.confirm'),
          cancelButtonText: this.$t('common.cancel'),
          type: 'warning'
        })
        await this.$store.dispatch('logout')
        this.isLoggedIn = false
        this.user = { nickname: '', avatar: '' }
        this.$message.success(this.$t('common.logoutSuccess'))
        this.$router.push('/login')
      } catch {
        // User cancelled
      }
    }
  }
}
</script>

<style scoped lang="scss">
@import '@/assets/styles/variables.scss';

.front-layout {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: $bg-body;
}

// ═══════════════════════════════════════════
//  Header (Glass Effect)
// ═══════════════════════════════════════════
.front-header {
  position: sticky;
  top: 0;
  z-index: $z-header;
  @include glass-effect;
  border-bottom: 1px solid $border-light;
  box-shadow: $shadow-header;

  .header-content {
    max-width: $content-max-width;
    margin: 0 auto;
    display: flex;
    align-items: center;
    padding: 0 $space-6;
    height: $header-height;
    gap: $space-6;
  }

  .header-logo {
    flex-shrink: 0;
  }

  .search-container {
    flex: 1;
    max-width: 420px;

    .search-input {
      ::v-deep .el-input__inner {
        background: $gray-50;
        border-radius: $radius-full !important;
        height: 40px;
        padding-left: 40px;
        border-color: transparent;

        &:focus {
          background: $white;
          border-color: $primary-light !important;
          box-shadow: 0 0 0 3px rgba($primary, 0.1) !important;
        }

        &:hover {
          background: $gray-100;
        }
      }

      ::v-deep .el-input__prefix {
        left: 14px;
      }

      ::v-deep .el-icon-search {
        font-size: 16px;
        color: $text-tertiary;
      }
    }
  }

  .header-nav {
    display: flex;
    align-items: center;
    gap: $space-4;
    flex-shrink: 0;

    .publish-btn {
      padding: 8px 18px !important;
      border-radius: $radius-full !important;

      i {
        font-size: 15px;
      }
    }

    .login-btn {
      border-radius: $radius-full !important;
      padding: 8px 24px !important;
    }

    .user-trigger {
      display: flex;
      align-items: center;
      gap: $space-2;
      cursor: pointer;
      padding: $space-1 $space-3 $space-1 $space-1;
      border-radius: $radius-full;
      transition: $transition-fast;

      &:hover {
        background: $gray-100;
      }

      &:active {
        transform: scale(0.97);
      }

      .user-avatar {
        background: $primary;
        color: $white;
        font-weight: $font-weight-semibold;
        flex-shrink: 0;
        transition: $transition-fast;
      }

      &:hover .user-avatar {
        box-shadow: 0 0 0 3px rgba($primary, 0.15);
      }

      .user-name {
        font-size: $font-sm;
        font-weight: $font-weight-medium;
        color: $text-primary;
        max-width: 80px;
        @include truncate;
      }

      .trigger-arrow {
        font-size: 12px;
        color: $text-tertiary;
        transition: $transition-fast;
      }

      &:hover .trigger-arrow {
        color: $text-secondary;
      }
    }
  }
}

// ═══════════════════════════════════════════
//  Main Content
// ═══════════════════════════════════════════
.main-content {
  flex: 1;
  max-width: $content-max-width;
  margin: 0 auto;
  width: 100%;
  padding: $space-6;

  &.no-header {
    margin: 0 auto;
  }
}

// ═══════════════════════════════════════════
//  Mobile Navigation
// ═══════════════════════════════════════════
.mobile-nav {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: $z-header;
  background: $bg-elevated;
  border-top: 1px solid $border-light;
  padding-bottom: env(safe-area-inset-bottom, 0);

  .mobile-nav-inner {
    display: flex;
    justify-content: space-around;
    align-items: flex-start;
    height: 60px;
    padding: 0 $space-2;
  }

  .nav-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 2px;
    padding: 6px 12px;
    cursor: pointer;
    color: $text-tertiary;
    transition: $transition-fast;
    position: relative;
    min-width: 56px;

    i {
      font-size: 22px;
      transition: $transition-fast;
    }

    span {
      font-size: 10px;
      font-weight: $font-weight-medium;
    }

    &.active {
      color: $primary;

      i {
        transform: scale(1.05);
      }
    }

    &:active:not(.publish) {
      transform: scale(0.92);
    }

    &.publish {
      margin-top: -16px;

      i {
        width: 48px;
        height: 48px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: $primary;
        color: $white;
        border-radius: $radius-full;
        font-size: 24px;
        box-shadow: 0 4px 12px rgba($primary, 0.35);
        transition: $transition-spring;
        animation: fabPulse 3s ease-in-out infinite;

        &:active {
          transform: scale(0.92);
        }
      }

      span {
        display: none;
      }
    }

    @keyframes fabPulse {
      0%, 100% { box-shadow: 0 4px 12px rgba($primary, 0.35); }
      50% { box-shadow: 0 4px 20px rgba($primary, 0.5); }
    }
  }
}

// ═══════════════════════════════════════════
//  Responsive
// ═══════════════════════════════════════════
@media (max-width: 768px) {
  .front-header {
    .header-content {
      padding: 0 $space-4;
      gap: $space-3;
    }

    .logo-text {
      display: none;
    }

    .search-container {
      max-width: none;

      .search-input ::v-deep .el-input__inner {
        background: $gray-100;
        height: 36px;
        font-size: $font-sm;
      }
    }

    .header-nav {
      .publish-btn span {
        display: none;
      }

      .publish-btn {
        padding: 8px 12px !important;
        min-width: auto !important;
      }

      .user-trigger .user-name {
        display: none;
      }
    }
  }

  .main-content {
    padding: $space-4;
    padding-bottom: calc(60px + env(safe-area-inset-bottom, 0));
  }
}
</style>
